'use client'
import type { MeResponse, UserResponse } from 'src/api/responses'

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { USER_DETAILS_KEY } from 'src/constants/session'
import { QUERY_KEYS } from 'src/constants/queries'
import { Me } from 'src/api/Me'

import useApi from './useApi'
import useAppStorage from './useAppStorage'
import useAuth from './useAuth'
import { useIntercom } from 'react-use-intercom'
import posthog from 'posthog-js'
const isUserResponse = (data: unknown | MeResponse): data is MeResponse => {
  if (typeof data !== 'object' || data === null) return false
  if (!('user' in data)) return false
  if (typeof data.user !== 'object' || data.user === null) return false
  if (!('id' in data.user) || typeof data.user.id !== 'string') return false
  return true
}
const useUserDetails = () => {
  const { setItem } = useAppStorage()
  const { isAuthenticated } = useAuth()
  const { update } = useIntercom()

  const meClient = useApi(Me)
  const [userDetails, setUserDetails] = useState<UserResponse | null>(null)

  const { data, isLoading, refetch: fetchUserDetails } = useQuery({
    queryKey: [QUERY_KEYS.USER_DETAILS],
    queryFn: async () => {
      const response = await meClient.whoAmI()
      const { data } = response
      if (!isUserResponse(data)) {
        throw new Error('Invalid user response format')
      }
      return data
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 15, // 15 minutes
  })

  useEffect(() => {
    if (isUserResponse(data)) {
      // Mirrors the user query *and* fires Intercom `update()` / `posthog.identify()`. The identify
      // calls are legitimately effects; splitting them is the fix, see docs/react-hooks-warnings.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUserDetails(data.user)
      setItem(USER_DETAILS_KEY, data)
      update({ userId: data.user.id, email: data.user.email, name: `${data.user.first_name} ${data.user.last_name}`, zIndex: 100 })
      posthog.identify(data.user.id, { email: data.user.email })
    }
  }, [data, setItem, update])

  return {
    // Falls back to the query data so `userDetails` is set in the same render `isLoading` flips,
    // not one render later via the mirroring effect. `useAppState` gates `initialize()` on both.
    userDetails: userDetails ?? data?.user ?? null,
    isLoading,
    fetchUserDetails,
  }
}
export default useUserDetails
